import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig, GridRow } from '@inavista/components/grid';
import { environment } from 'projects/service/src/environments/environment';
import { ACTION_ICON_SVG } from 'projects/service/src/app/modules/service-order/pages/main-page/service-order.grid.config';

// Cycle Counts page on @inavista/components. Same columns, labels, default visibility, status colours and actions as
// the Syncfusion grid it replaces. Rows come from api/grid/cycle-counts (RIMS API CycleCountsGridController): the cycle
// counts of the technicians the employee in the login token may see, paged in SQL.

const BLACK = '#000000de';
const GREEN = '#5cb85c';
const ORANGE = '#ff8403';
const INACTIVE = '#cccccc';
const SCHEDULE_ICON = '<rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><polyline points="9 16 11 18 15 14" />';
const COST_ANALYSIS_ICON = '<rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" />';
const DOWNLOAD_ICON = '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />';
const HISTORY_ICON = '<path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><polyline points="3 3 3 8 8 8" /><polyline points="12 7 12 12 15 14" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';
const PLUS_ICON = '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />';

export const ADD_CYCLE_COUNT_ACTION = 'add-cycle-count';

// The statuses in which the start date may be changed: the cycle count popup's rule (its start date picker). The old
// grid let a start date be picked on any row, and the save sets the status to Planning, so a Closed cycle count went
// back to Planning (LEFT.md F-17).
const START_DATE_STATUSES = ['Open', 'Planning', 'Scheduled'];

export interface CycleCountsGridContext {
  roleClaims: Record<string, { visible?: boolean; edit?: boolean }>;
  statusColor: (value: unknown) => string;
  startDateEditor: TemplateRef<unknown>;
}

// Why an action is not available on this row, or null when it is (the old grid greyed these out, without a reason).
export function actionDisabledReason(key: string, row: GridRow): string | null {
  const status = row['status'] as string;
  if (key === 'schedule') {
    if (status !== 'Planning') return 'Only a cycle count in Planning can be scheduled.';
    if (row['startDate'] == null) return 'Set a start date before scheduling.';
  }
  if (key === 'delete' && status !== 'Open') return 'Only an Open cycle count can be deleted.';
  return null;
}

// The old command column, same order: Schedule, Details, Cost Analysis, Attachment, Comment, History, Delete.
export function buildCycleCountsActionIcons(): ActionIconDef[] {
  const gated = (key: string, label: string, icon: string): ActionIconDef => ({
    key, label, icon,
    colorFn: row => (actionDisabledReason(key, row) ? INACTIVE : BLACK),
    disabledFn: row => !!actionDisabledReason(key, row),
    labelFn: row => actionDisabledReason(key, row) ?? label,
  });
  return [
    gated('schedule', 'Schedule', SCHEDULE_ICON),
    { key: 'details', label: 'Details', colorFn: () => BLACK, icon: ACTION_ICON_SVG.details },
    { key: 'costAnalysis', label: 'Cost Analysis', colorFn: () => BLACK, icon: COST_ANALYSIS_ICON },
    // Green with an attachment, orange with a comment, as before.
    { key: 'attachments', label: 'Attachment', colorFn: row => (Number(row['attachmentCount']) > 0 ? GREEN : BLACK), icon: DOWNLOAD_ICON },
    { key: 'comments', label: 'Comment', colorFn: row => (Number(row['commentCount']) > 0 ? ORANGE : BLACK), icon: ACTION_ICON_SVG.comments },
    { key: 'history', label: 'History', colorFn: () => BLACK, icon: HISTORY_ICON },
    gated('delete', 'Delete Cycle Count', TRASH_ICON),
  ];
}

const SHOWN = [
  'cycleCountId', 'cycleCountNumber', 'serviceDepartment', 'technicianName', 'warehouse', 'address', 'city', 'state', 'zipCode',
  'phone', 'startDate', 'createdDate', 'endDate', 'duration', 'status', 'totalCost', 'totalExtCost',
];
// The query's other columns: the action rules read them, the old grid never showed them.
const BACKING_COLUMNS = [
  'id', 'technicianId', 'createdBy', 'lastUpdatedBy', 'lastUpdatedDate', 'commentCount', 'attachmentCount', 'openExceptionCount',
];
const CENTRED = ['cycleCountId', 'cycleCountNumber', 'warehouse', 'state', 'zipCode'];
const RIGHT = ['duration', 'totalCost', 'totalExtCost'];
const DATES = ['startDate', 'createdDate', 'endDate'];
const money = (value: unknown) => (value == null || value === '' ? ''
  : `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export function buildCycleCountsGridConfig(ctx: CycleCountsGridContext): GridConfig {
  const canEditStartDate = !!ctx.roleClaims['CycleCount_StartDate']?.edit;
  return {
    apiBaseUrl: `${environment.apiUrl}grid/cycle-counts`,
    keyColumn: 'cycleCountId',
    title: 'Cycle Counts',
    rowHeight: 26,
    loadingIndicator: 'banner',
    defaultColumnOrder: SHOWN,
    hiddenByDefaultColumns: BACKING_COLUMNS,
    startHiddenColumns: ['serviceDepartment', 'technicianName', 'address', 'city', 'state', 'zipCode', 'phone', 'endDate'],
    columnLabels: {
      cycleCountId: 'Order ID', cycleCountNumber: 'Cycle Count No.', serviceDepartment: 'Service Department',
      technicianName: 'Technician', warehouse: 'Warehouse', address: 'Address', city: 'City', state: 'State', zipCode: 'Zip code',
      phone: 'Phone', startDate: 'Start Date', createdDate: 'Created Date', endDate: 'End Date', duration: 'Duration',
      status: 'Status', totalCost: 'Cost VAR (LN - ADJ)', totalExtCost: 'Ext Cost (LN)',
    },
    // RIMS grid look: headers left; text and dates left; codes and IDs centred; amounts right.
    columnAlign: Object.fromEntries(SHOWN.map(c => [c, {
      header: { horizontal: 'left' as const },
      ...(CENTRED.includes(c) ? { horizontal: 'center' as const } : RIGHT.includes(c) ? { horizontal: 'right' as const } : {}),
    }])),
    columnWidths: {
      // The default columns fit a 1600px window without a sideways scroll.
      cycleCountId: 110, cycleCountNumber: 120, serviceDepartment: 150, technicianName: 170, warehouse: 110, address: 220,
      city: 130, state: 80, zipCode: 100, phone: 130, startDate: 125, createdDate: 120, endDate: 120, duration: 100,
      status: 110, totalCost: 140, totalExtCost: 130,
    },
    columnDateFormats: Object.fromEntries(DATES.map(c => [c, 'd MMM, yyyy'])),
    columnTypes: Object.fromEntries(DATES.map(c => [c, 'date' as const])),
    // As before: the duration (days between start and end, the procedure's value) followed by "Hours" (LEFT.md F-18),
    // the two costs as dollars with two decimals.
    columnFormatters: {
      duration: value => (value == null || value === '' || Number(value) === 0 ? '' : `${value} Hours`),
      totalCost: money,
      totalExtCost: money,
    },
    columnColors: { status: ctx.statusColor },
    // Order ID opens the cycle count, as before.
    hyperlinkColumns: ['cycleCountId'],
    // The old checkbox (Excel) filters.
    excelFilterColumns: ['technicianName', 'warehouse', 'status'],
    enableContextMenuFilter: true,
    defaultSort: { column: 'createdDate', direction: 'desc' },
    // Excel export for everyone, as before (no role rule): "Cycle count(<date>).xlsx".
    enableExport: true,
    enableRefresh: true,
    enableFitColumns: true,
    enableSavedViews: true,
    enableResetLayout: true,
    wrapHeaders: true,
    toolbarActions: ctx.roleClaims['CycleCountGrid_Toolbar_AddCycleCount']?.visible
      ? [{ key: ADD_CYCLE_COUNT_ACTION, label: 'Add Cycle Count', kind: 'button' as const, icon: PLUS_ICON }]
      : [],
    // Start Date is changed in the grid by the roles with CycleCount_StartDate edit (double-click the row, as before),
    // on a cycle count still Open, Planning or Scheduled; the save sets it to Planning (UpdateCycleCountStartDate).
    ...(canEditStartDate ? {
      editMode: 'inline' as const,
      editTrigger: 'doubleClick' as const,
      editSaveMode: 'row' as const,
      editableColumns: ['startDate'],
      readOnlyCellFn: (row: GridRow) => !START_DATE_STATUSES.includes(row['status'] as string),
      requiredColumns: [],
      editorTemplates: { startDate: ctx.startDateEditor },
      rowDeletable: () => false,
    } : {}),
    emptyStateMessage: 'No cycle counts to show.',
    persistenceKey: 'rims.service.cycle-counts',
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
    persistenceVersion: 1,
  };
}
